import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

/**
 * Provides authentication state and helpers to the component tree.
 *
 * Sprint 2 av Fluxer-migrationen utökar kontexten med:
 *   - loginSteam(): standalone Steam-login utan Discord
 *   - validateInvite(code): kontrollera invite-kod (publik, ingen auth)
 *   - redeemInvite(code): lös in invite-kod mot inloggad användare
 *   - accessStatus: läses från user — 'active', 'pending', 'suspended', 'banned'
 *   - isPending/isActive: bekväma booleans för UI-grindning
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('jwt_token'));

  const fetchUser = useCallback(async (jwt) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Ensure discord_id is always a string (BigInt precision loss in JSON)
        if (data.discord_id != null) data.discord_id = String(data.discord_id);
        setUser(data);
      } else {
        // Token invalid
        localStorage.removeItem('jwt_token');
        setToken(null);
        setUser(null);
      }
    } catch (err) {
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  /**
   * Discord OAuth login (legacy / fortfarande primär för existerande
   * community-medlemmar). Redirectar bort sidan.
   */
  const login = useCallback(() => {
    const currentUrl = window.location.origin;
    window.location.href = `${API_URL}/api/auth/login?redirect=${encodeURIComponent(currentUrl)}`;
  }, []);

  /**
   * Steam OpenID login utan Discord (Sprint 2 av Fluxer-migrationen).
   * Nya signups landar som access_status='pending' och måste lösa in
   * en invite-kod (eller godkännas manuellt) för att bli active.
   */
  const loginSteam = useCallback(() => {
    const currentUrl = window.location.origin;
    window.location.href = `${API_URL}/api/auth/login-steam?redirect=${encodeURIComponent(currentUrl)}`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('jwt_token');
    setToken(null);
    setUser(null);
  }, []);

  const handleCallback = useCallback((jwt) => {
    localStorage.setItem('jwt_token', jwt);
    setToken(jwt);
    fetchUser(jwt);
  }, [fetchUser]);

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
  }, [token]);

  /**
   * Validera en invite-kod utan att lösa in den. Används för UI-feedback
   * innan inlösen. Returnerar { valid, reason, auto_approve, note,
   * remaining_uses }.
   */
  const validateInvite = useCallback(async (code) => {
    const response = await fetch(`${API_URL}/api/auth/invite/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return response.json();
  }, []);

  /**
   * Lös in en invite-kod mot inloggad användare. Vid auto_approve blir
   * access_status='active' direkt — vi refetchar user-objektet så UI
   * uppdateras. Returnerar { ok, access_status, granted_active }.
   */
  const redeemInvite = useCallback(async (code) => {
    if (!token) {
      return { ok: false, error: 'not_authenticated' };
    }
    const response = await fetch(`${API_URL}/api/auth/invite/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ code }),
    });
    const data = await response.json();
    if (response.ok && data.ok) {
      // Refetcha user så access_status uppdateras direkt i UI
      await fetchUser(token);
    }
    return { status: response.status, ...data };
  }, [token, fetchUser]);

  const accessStatus = user?.access_status || null;
  const isActive = accessStatus === 'active';
  const isPending = accessStatus === 'pending';

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginSteam,
        logout,
        handleCallback,
        getAuthHeaders,
        token,
        validateInvite,
        redeemInvite,
        accessStatus,
        isActive,
        isPending,
        isLoggedIn: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
